import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
// import AddIcon from "@mui/icons-material/Add";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
// import RemoveIcon from "@mui/icons-material/Remove";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { borderRadiusPx } from "../../../../../theme";

import { toast } from "../../../../Components/ui";
import {
  // THREE_SLOT_CONFIG,
  JACKPOT_DEFAULTS,
  JACKPOT_TIER_THEMES,
  SIDE_BADGES,
  SLOT_MACHINE_FRAME_THEME,
  getSymbolsByCategory,
  getRandomSymbol,
  calculateWinResult,
} from "./threeSlotConfig";
import threeSlotBg from "../../../../../static/Images/newlyAdded/3_slot_game.png";
import ThreeSlotSymbolIcon from "./ThreeSlotSymbolIcon";
import ThreeSlotPaytableModal from "./ThreeSlotPaytableModal";
import ThreeSlotDepositModal from "./ThreeSlotDepositModal";
import ThreeSlotWinModal from "./ThreeSlotWinModal";
import FailedPopup from "../../../../Components/FailedPopup";
import { slotAudio } from "./slotAudio";

export const ThreeSlotMachineDetailCard = ({
  lotteryData,
  account, onSpin, spinLoading = false, lastSpinResult,
  balance: parentBalance,
  win: parentWin,
  setBalance: setParentBalance,
  setWin: setParentWin,
}) => {
  const category =
    lotteryData?.slotCategory ||
    (lotteryData?.name?.toLowerCase().includes("crypto") ? "crypto" : "fiat");
  const currency = lotteryData?.currency || "BET";
  const activeSymbols = getSymbolsByCategory(category);

  // Game States: User BET balance from wallet
  const [balance, setBalance] = useState(() => {
    const init = parseFloat(parentBalance);
    return !isNaN(init) ? init.toFixed(2) : "0.00";
  });
  const [isSpinning, setIsSpinning] = useState(false);

  // Sync when parentBalance from wallet updates
  useEffect(() => {
    if (!isSpinning && parentBalance !== undefined && parentBalance !== null) {
      setBalance(parentBalance);
    }
  }, [parentBalance, isSpinning]);

  const [currentWin, setCurrentWin] = useState("0.00");
  // Bet is fixed by the machine ticketPrice from the contract
  // const [betIndex, setBetIndex] = useState(2); // default: 5.00
  const betAmount = parseFloat(lotteryData?.ticketPrice) || 1.0;

  // 3-Reel Symbol Display State: Each reel has [topSymbol, mainSymbol, bottomSymbol]
  const [reelDisplay, setReelDisplay] = useState([
    [activeSymbols[0], activeSymbols[1], activeSymbols[2]],
    [activeSymbols[2], activeSymbols[3], activeSymbols[4]],
    [activeSymbols[4], activeSymbols[0], activeSymbols[1]],
  ]);

  const [spinningReels, setSpinningReels] = useState([false, false, false]);
  const [isAutoSpinning, setIsAutoSpinning] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Bonus & Free Spins (UI commented out)
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0);
  // const [totalFreeSpins, setTotalFreeSpins] = useState(10);

  // Jackpots (Mega, Major, Mini)
  const [jackpots, setJackpots] = useState({
    mega: parseFloat(lotteryData?.jackpots?.mega?.amount) || JACKPOT_DEFAULTS.mega,
    major: parseFloat(lotteryData?.jackpots?.major?.amount) || JACKPOT_DEFAULTS.major,
    mini: parseFloat(lotteryData?.jackpots?.mini?.amount) || JACKPOT_DEFAULTS.mini,
  });

  // Sync local jackpots when parent fetches live blockchain data
  useEffect(() => {
    if (lotteryData?.jackpots && !isNaN(parseFloat(lotteryData.jackpots.mini.amount))) {
      setJackpots({
        mega: parseFloat(lotteryData.jackpots.mega.amount),
        major: parseFloat(lotteryData.jackpots.major.amount),
        mini: parseFloat(lotteryData.jackpots.mini.amount),
      });
    }
  }, [lotteryData?.jackpots]);

  // Modals
  const [paytableOpen, setPaytableOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [winModalOpen, setWinModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Failed / Error Popup State
  const [failedModal, setFailedModal] = useState({
    open: false,
    title: "Insufficient Balance",
    message: "You don't have enough BET tokens to place this bet.",
    description: "Please deposit or acquire BET tokens in your wallet before spinning.",
  });

  // Sync parent balance if provided
  useEffect(() => {
    if (setParentBalance) setParentBalance(balance);
  }, [balance, setParentBalance]);

  useEffect(() => {
    if (setParentWin) setParentWin(currentWin);
  }, [currentWin, setParentWin]);

  // Jackpot formatting (updated only and only from contract)
  const formatJackpot = (val) => {
    const num = Number(val);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Quick Bet Adjusters - Commented out since bet is fixed
  // const handleDecreaseBet = () => {
  //   slotAudio.playClick();
  //   if (betIndex > 0) {
  //     setBetIndex((prev) => prev - 1);
  //   }
  // };
  //
  // const handleIncreaseBet = () => {
  //   slotAudio.playClick();
  //   if (betIndex < THREE_SLOT_CONFIG.betSteps.length - 1) {
  //     setBetIndex((prev) => prev + 1);
  //   }
  // };

  const handleToggleSound = () => {
    const muted = slotAudio.toggleMute();
    setIsMuted(muted);
    toast.info(muted ? "Sound Muted" : "Sound Enabled", { autoClose: 1200 });
  };


  const spinIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
    };
  }, []);

  // Main Spin Logic
  const handleSpin = useCallback(async () => {
    if (isSpinning || spinLoading) return;

    const currentBal = parseFloat(balance);
    const isFreeSpin = freeSpinsRemaining > 0;

    if (!isFreeSpin && currentBal < betAmount) {
      setIsAutoSpinning(false);
      setFailedModal({
        open: true,
        title: "Insufficient Balance",
        message: `Your balance is ${parseFloat(balance || 0).toFixed(2)} BET, but this game requires ${betAmount.toFixed(2)} BET.`,
        description: "Please deposit or acquire BET tokens in your wallet before spinning.",
      });
      return;
    }

    let hasStartedSpinning = false;
    const startReelRotation = () => {
      if (hasStartedSpinning) return;
      hasStartedSpinning = true;

      // Deduct bet if not free spin
      if (!isFreeSpin) {
        setBalance((prev) => (parseFloat(prev) - betAmount).toFixed(2));
      } else {
        setFreeSpinsRemaining((prev) => prev - 1);
      }

      setIsSpinning(true);
      setSpinningReels([true, true, true]);
      setCurrentWin("0.00");
      setLastResult(null);
      setWinModalOpen(false);

      // Start rapid rotation interval immediately upon tx confirmation
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = setInterval(() => {
        slotAudio.playSpinTick();
        setReelDisplay([
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
        ]);
      }, 90);
    };

    let chainSpinResult = null;

    if (onSpin && typeof onSpin === "function" && !isFreeSpin) {
      chainSpinResult = await onSpin(lotteryData, betAmount, startReelRotation);

      if (!chainSpinResult) {
        setIsAutoSpinning(false);
        if (hasStartedSpinning) {
          if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
          setSpinningReels([false, false, false]);
          setIsSpinning(false);
        }
        return;
      }
    } else {
      // Free spins or offline demo mode: start immediately
      startReelRotation();
    }

    // Ensure reels are rotating in case onTxConfirmed didn't fire
    if (!hasStartedSpinning) {
      startReelRotation();
    }

    let finalCenter;

    if (chainSpinResult?.reels && Array.isArray(chainSpinResult.reels)) {
      finalCenter = chainSpinResult.reels.map((reelIndex) => {
        const safeIndex = Number(reelIndex) % activeSymbols.length;
        return activeSymbols[safeIndex];
      });
    } else {
      finalCenter = [
        getRandomSymbol(category),
        getRandomSymbol(category),
        getRandomSymbol(category),
      ];
    }

    // Generate full final reels [top, center, bottom]
    const finalReels = finalCenter.map((centerSym) => [
      getRandomSymbol(category),
      centerSym,
      getRandomSymbol(category),
    ]);

    // Step 1: Stop Reel 1 (650ms from result arrival)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, true, true]);
      setReelDisplay((prev) => [finalReels[0], prev[1], prev[2]]);
    }, 650);

    // Step 2: Stop Reel 2 (1250ms from result arrival)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, false, true]);
      setReelDisplay((prev) => [finalReels[0], finalReels[1], prev[2]]);
    }, 1250);

    // Step 3: Stop Reel 3 (1850ms from result arrival) & evaluate outcome
    setTimeout(() => {
      slotAudio.playReelStop();
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
      setSpinningReels([false, false, false]);
      setReelDisplay(finalReels);
      setIsSpinning(false);

      let outcome;

      if (chainSpinResult?.result) {
        const payoutAmount = parseFloat(chainSpinResult.payout || "0");
        const megaAmt = parseFloat(jackpots?.mega) || 0;
        const majorAmt = parseFloat(jackpots?.major) || 0;
        const miniAmt = parseFloat(jackpots?.mini) || 0;

        const isMega = payoutAmount >= megaAmt && megaAmt > 0;
        const isMajor = payoutAmount >= majorAmt && majorAmt > 0;
        const isMini = payoutAmount >= miniAmt && miniAmt > 0;
        const isJackpot = isMega || isMajor || isMini;

        let label = "WINNER!";
        let jackpotTier = null;
        if (isMega) {
          label = "MEGA JACKPOT WON!";
          jackpotTier = "mega";
        } else if (isMajor) {
          label = "MAJOR JACKPOT WON!";
          jackpotTier = "major";
        } else if (isMini) {
          label = "MINI JACKPOT WON!";
          jackpotTier = "mini";
        } else if (payoutAmount > 0) {
          label = "WINNING SPIN!";
        } else {
          label = "NO WIN";
        }

        outcome = {
          isWin: payoutAmount > 0,
          amount: payoutAmount,
          betAmount: betAmount,
          type: isJackpot ? "jackpot" : payoutAmount > 0 ? "win" : "none",
          jackpotTier,
          label,
          symbols: finalCenter,
          requestId: chainSpinResult.requestId,
        };
      } else {
        outcome = calculateWinResult(finalCenter, betAmount, isFreeSpin, jackpots);
        outcome.betAmount = betAmount;
      }

      setLastResult(outcome);

      if (outcome.isWin) {
        if (outcome.type === "jackpot") {
          slotAudio.playJackpot();
        } else {
          slotAudio.playWin();
        }

        const winFormatted = outcome.amount.toFixed(2);
        setCurrentWin(winFormatted);
        setBalance((prev) => (parseFloat(prev) + outcome.amount).toFixed(2));

        if (outcome.type === "freespins") {
          const spinsWon = outcome.freeSpinsWon || 5;
          setFreeSpinsRemaining((prev) => prev + spinsWon);
          // setTotalFreeSpins(spinsWon);
        }
      } else {
        slotAudio.playLose();
      }

      // Show win/loss popup modal instead of toast
      setWinModalOpen(true);
    }, 1850);
  }, [
    isSpinning,
    spinLoading,
    balance,
    betAmount,
    freeSpinsRemaining,
    category,
    jackpots,
    lotteryData,
    onSpin,
    activeSymbols,
  ]);


  const spinRef = useRef(handleSpin);
  useEffect(() => {
    spinRef.current = handleSpin;
  }, [handleSpin]);

  // Autoplay handler
  useEffect(() => {
    let timer;
    if (isAutoSpinning && !isSpinning) {
      timer = setTimeout(() => {
        if (isAutoSpinning) {
          spinRef.current();
        }
      }, 1200);
    }
    return () => clearTimeout(timer);
  }, [isAutoSpinning, isSpinning]);

  const handleToggleAutoPlay = () => {
    slotAudio.playClick();
    if (isAutoSpinning) {
      setIsAutoSpinning(false);
      toast.info("Autoplay Stopped", { autoClose: 1500 });
    } else {
      setIsAutoSpinning(true);
      toast.success("Autoplay Started!", { autoClose: 1500 });
      if (!isSpinning) {
        handleSpin();
      }
    }
  };

  return (
    <Box
      className="slot-game-app pokies-game-app"
      sx={{
        width: "100%",
        maxWidth: "100%",
        margin: "0 auto",
        bgcolor: SLOT_MACHINE_FRAME_THEME.bg,
        borderRadius: SLOT_MACHINE_FRAME_THEME.borderRadius,
        boxShadow: SLOT_MACHINE_FRAME_THEME.boxShadow,
        overflow: "hidden",
        position: "relative",
        border: SLOT_MACHINE_FRAME_THEME.border,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Platform Consistent Top Accent Gradient Bar (3-Slot Signature Purple & Gold) */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(90deg, #8b5cf6 0%, #a855f7 40%, #eab308 100%)",
        }}
      />

      {/* 1. TOP APP HEADER BAR */}
      <Box
        className="slot-header-bar pokies-header-bar"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          bgcolor: SLOT_MACHINE_FRAME_THEME.headerBg,
          borderBottom: "1px solid #e2e8f0",
          zIndex: 10,
        }}
      >
        {/* Left: 3-SlotBET Game Badge */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md || "8px",
                bgcolor: "#faf5ff",
                color: "#8b5cf6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e9d5ff",
                fontSize: "1.1rem",
              }}
            >
              🎰
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.95rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.1,
                  letterSpacing: "-0.2px",
                }}
              >
                {lotteryData?.name || "3-SlotBET"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: "#8b5cf6",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                  mt: "2px",
                  textTransform: "uppercase",
                }}
              >
                {category === "crypto" ? "Crypto Slot • 3-Reel Edition" : "Classic Slot • 3-Reel Edition"}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Right: Sound Toggle */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={{
              color: "#64748b",
              p: 0.75,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" },
            }}
          >
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 20 }} /> : <VolumeUpIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Box>

      {/* 2. GAME STAGE (Clean Light Atmosphere consistent with platform cards) */}
      <Box
        className="slot-stage-wrapper pokies-stage-wrapper"
        sx={{
          position: "relative",
          backgroundImage: `url(${threeSlotBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 2.5, sm: 3.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(248, 250, 252, 0.90) 50%, rgba(241, 245, 249, 0.95) 100%)",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 0%, rgba(234, 179, 8, 0.08) 0%, transparent 60%), radial-gradient(circle at 10% 40%, rgba(139, 92, 246, 0.06) 0%, transparent 50%), radial-gradient(circle at 90% 40%, rgba(59, 130, 246, 0.06) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          {/* 3. PROGRESSIVE JACKPOT TIERS (MEGA, MAJOR, MINI) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: { xs: 0.75, sm: 1.5 },
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {/* MEGA JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.mega.borderRadius,
                background: JACKPOT_TIER_THEMES.mega.background,
                border: JACKPOT_TIER_THEMES.mega.border,
                boxShadow: JACKPOT_TIER_THEMES.mega.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mega.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.mega)}
              </Typography>
            </Box>

            {/* MAJOR JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.major.borderRadius,
                background: JACKPOT_TIER_THEMES.major.background,
                border: JACKPOT_TIER_THEMES.major.border,
                boxShadow: JACKPOT_TIER_THEMES.major.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.major.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.major)}
              </Typography>
            </Box>

            {/* MINI JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.mini.borderRadius,
                background: JACKPOT_TIER_THEMES.mini.background,
                border: JACKPOT_TIER_THEMES.mini.border,
                boxShadow: JACKPOT_TIER_THEMES.mini.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mini.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.mini)}
              </Typography>
            </Box>
          </Box>

          {/* 4. REELS CASINO FRAME & SIDE PAYLINE INDICATOR PILLARS */}
          <Box
            className="slot-reels-container pokies-reels-container"
            sx={{
              position: "relative",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)",
              p: "3px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
              mb: 1.75,
            }}
          >
            <Box
              sx={{
                borderRadius: "12px",
                bgcolor: "#ffffff",
                border: "1.5px solid #e2e8f0",
                display: "flex",
                position: "relative",
                overflow: "hidden",
                boxShadow: "inset 0 2px 6px rgba(0, 0, 0, 0.03)",
              }}
            >
              {/* LEFT SIDE PAYLINE INDICATOR STRIP */}
              <Box
                sx={{
                  width: { xs: 24, sm: 28 },
                  bgcolor: "#f8fafc",
                  borderRight: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  py: 1,
                  alignItems: "center",
                }}
              >
                {SIDE_BADGES.map((b, i) => (
                  <Box
                    key={`l-${i}`}
                    sx={{
                      width: { xs: 18, sm: 21 },
                      height: { xs: 18, sm: 21 },
                      borderRadius: "4px",
                      bgcolor: b.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.62rem", sm: "0.7rem" },
                        fontWeight: 900,
                        color: "#ffffff",
                        lineHeight: 1,
                      }}
                    >
                      {b.num}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* 3 SLOT REELS GRID */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "0px",
                  bgcolor: "#ffffff",
                  position: "relative",
                }}
              >
                {/* CENTER PAYLINE INDICATOR LINE */}
                <Box
                  className="slot-center-payline pokies-center-payline"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    height: 2,
                    background: "linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #ef4444 100%)",
                    transform: "translateY(-50%)",
                    zIndex: 6,
                    pointerEvents: "none",
                    boxShadow: "0 0 8px rgba(245, 158, 11, 0.6)",
                    opacity: 0.8,
                  }}
                />

                {/* 3 REEL COLUMNS (SEAMLESS 3D MECHANICAL ROLLER DRUM CYLINDERS) */}
                {[0, 1, 2].map((reelIdx) => {
                  const isThisReelSpinning = spinningReels[reelIdx];
                  const reelSymbols = reelDisplay[reelIdx];

                  return (
                    <Box
                      key={`reel-${reelIdx}`}
                      className={`roller-drum-wheel ${isThisReelSpinning ? "is-rolling" : ""}`}
                      sx={{
                        position: "relative",
                        bgcolor: "#f1f5f9",
                        background:
                          "linear-gradient(90deg, rgba(100, 116, 139, 0.22) 0%, rgba(241, 245, 249, 0.6) 6%, transparent 16%, transparent 84%, rgba(241, 245, 249, 0.6) 94%, rgba(100, 116, 139, 0.22) 100%), linear-gradient(180deg, #94a3b8 0%, #cbd5e1 7%, #e2e8f0 16%, #f8fafc 32%, #ffffff 50%, #f8fafc 68%, #e2e8f0 84%, #cbd5e1 93%, #94a3b8 100%)",
                        borderRight: reelIdx < 2 ? "2px solid #cbd5e1" : "none",
                        boxShadow: reelIdx < 2
                          ? "inset -2px 0 4px rgba(0,0,0,0.08), 1px 0 0 rgba(255,255,255,0.8)"
                          : "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-around",
                        py: { xs: 1, sm: 1.5 },
                        minHeight: { xs: 350, sm: 410, md: 440 },
                        overflow: "hidden",
                      }}
                    >
                      {/* Top Curved Roller Hood Vignette Shadow */}
                      <Box
                        className="roller-top-vignette"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: { xs: 65, sm: 80 },
                          background:
                            "linear-gradient(180deg, rgba(15, 23, 42, 0.5) 0%, rgba(15, 23, 42, 0.25) 35%, rgba(15, 23, 42, 0.05) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 8,
                        }}
                      />

                      {/* Bottom Curved Roller Hood Vignette Shadow */}
                      <Box
                        className="roller-bottom-vignette"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: { xs: 65, sm: 80 },
                          background:
                            "linear-gradient(0deg, rgba(15, 23, 42, 0.5) 0%, rgba(15, 23, 42, 0.25) 35%, rgba(15, 23, 42, 0.05) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 8,
                        }}
                      />

                      {/* Center Cylinder Horizontal Gloss Sheen */}
                      <Box
                        className="roller-center-gloss"
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: 0,
                          right: 0,
                          height: { xs: 110, sm: 135 },
                          transform: "translateY(-50%)",
                          background:
                            "linear-gradient(180deg, transparent 0%, rgba(255, 255, 255, 0.2) 30%, rgba(255, 255, 255, 0.45) 50%, rgba(255, 255, 255, 0.2) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 4,
                        }}
                      />

                      {/* Top Symbol (Curved Upper Cylinder Slope) */}
                      <Box
                        sx={{
                          opacity: 0.68,
                          transform: "perspective(600px) rotateX(15deg) scale(0.88)",
                          transformOrigin: "bottom center",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                          zIndex: 2,
                        }}
                      >
                        <ThreeSlotSymbolIcon symbol={reelSymbols[0]} size="md" />
                      </Box>

                      {/* Main Center Symbol (Highlighted Front Apex Crest) */}
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 5,
                          my: { xs: 0.25, sm: 0.5 },
                          filter: isThisReelSpinning ? "blur(4px)" : "none",
                          transform: isThisReelSpinning ? "scale(0.96)" : "scale(1)",
                          transition:
                            "filter 0.15s ease, transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        }}
                      >
                        <ThreeSlotSymbolIcon
                          symbol={reelSymbols[1]}
                          size="lg"
                          isWinning={!isSpinning && lastResult?.isWin}
                        />
                      </Box>

                      {/* Bottom Symbol (Curved Lower Cylinder Slope) */}
                      <Box
                        sx={{
                          opacity: 0.68,
                          transform: "perspective(600px) rotateX(-15deg) scale(0.88)",
                          transformOrigin: "top center",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                          zIndex: 2,
                        }}
                      >
                        <ThreeSlotSymbolIcon symbol={reelSymbols[2]} size="md" />
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* RIGHT SIDE PAYLINE INDICATOR STRIP */}
              <Box
                sx={{
                  width: { xs: 24, sm: 28 },
                  bgcolor: "#f8fafc",
                  borderLeft: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  py: 1,
                  alignItems: "center",
                }}
              >
                {SIDE_BADGES.map((b, i) => (
                  <Box
                    key={`r-${i}`}
                    sx={{
                      width: { xs: 18, sm: 21 },
                      height: { xs: 18, sm: 21 },
                      borderRadius: "4px",
                      bgcolor: b.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.62rem", sm: "0.7rem" },
                        fontWeight: 900,
                        color: "#ffffff",
                        lineHeight: 1,
                      }}
                    >
                      {b.num}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* 5. DASHBOARD BAR (BALANCE | BET) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
              gap: { xs: 1, sm: 2 },
              p: { xs: 1.25, sm: 1.75 },
              borderRadius: "12px",
              bgcolor: "#ffffff",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {/* BALANCE - Live BET token balance from user wallet */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                BALANCE
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.02rem" },
                  fontWeight: 900,
                  color: "#0f172a",
                  fontFamily: "monospace",
                }}
              >
                {currency} {parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            {/* BET STEPPER - Fixed bet: minus & plus commented out */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                BET
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.75}
                sx={{ mt: "1px" }}
              >
                {/* Bet is fixed - Minus button commented out
                <Box
                  onClick={handleDecreaseBet}
                  sx={{
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    borderRadius: "4px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#e2e8f0" },
                  }}
                >
                  <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#334155" }} />
                </Box>
                */}
                <Typography
                  sx={{
                    fontSize: { xs: "0.78rem", sm: "1.02rem" },
                    fontWeight: 900,
                    color: "#0f172a",
                    fontFamily: "monospace",
                    minWidth: { xs: 48, sm: 60 },
                  }}
                >
                  {currency} {betAmount.toFixed(2)}
                </Typography>
                {/* Bet is fixed - Plus button commented out
                <Box
                  onClick={handleIncreaseBet}
                  sx={{
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    borderRadius: "4px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#e2e8f0" },
                  }}
                >
                  <AddIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#334155" }} />
                </Box>
                */}
              </Stack>
            </Box>

            {/* WIN AMOUNT - Commented out since contract does not return win metadata
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                WIN
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.02rem" },
                  fontWeight: 900,
                  color: parseFloat(currentWin) > 0 ? "#16a34a" : "#0f172a",
                  fontFamily: "monospace",
                }}
              >
                {currency} {currentWin}
              </Typography>
            </Box>
            */}

            {/* FREE SPINS BOX - Commented out since contract does not have free spins
            <Box
              sx={{
                textAlign: "center",
                borderRadius: "6px",
                background:
                  freeSpinsRemaining > 0
                    ? "linear-gradient(135deg, #fef3c7 0%, #fee2e2 100%)"
                    : "#f8fafc",
                border: freeSpinsRemaining > 0 ? "1px solid #f59e0b" : "1px solid #e2e8f0",
                p: "2px",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>🎁</Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.52rem", sm: "0.62rem" },
                    fontWeight: 900,
                    color: freeSpinsRemaining > 0 ? "#b45309" : "#64748b",
                    lineHeight: 1,
                  }}
                >
                  FREE SPINS
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1rem" },
                  fontWeight: 900,
                  color: freeSpinsRemaining > 0 ? "#991b1b" : "#334155",
                  lineHeight: 1.1,
                  fontFamily: "monospace",
                }}
              >
                {freeSpinsRemaining} / {totalFreeSpins}
              </Typography>
            </Box>
            */}
          </Box>

          {/* 6. MAIN ACTION BAR (PAYTABLE | BIG 3D SPIN | AUTOPLAY) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 0.5, sm: 1.5 },
              gap: { xs: 2, sm: 4 },
            }}
          >
            {/* PAYTABLE BUTTON */}
            <Box
              onClick={() => {
                slotAudio.playClick();
                setPaytableOpen(true);
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: 140, sm: 180 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                border: "1.5px solid #a78bfa",
                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.1)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <FormatListBulletedIcon sx={{ color: "#ffffff", fontSize: { xs: 20, sm: 22 } }} />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                PAYTABLE
              </Typography>
            </Box>

            {/* QUICK BET MINUS - Commented out since bet is fixed
            <Box
              onClick={handleDecreaseBet}
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                bgcolor: "#f8fafc",
                border: "1.5px solid #cbd5e1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
                "&:active": { transform: "scale(0.92)" },
              }}
            >
              <RemoveIcon sx={{ color: "#0f172a", fontSize: { xs: 26, sm: 28 } }} />
            </Box>
            */}

            {/* BIG 3D RED SPIN BUTTON */}
            <Box
              onClick={handleSpin}
              sx={{
                width: { xs: 92, sm: 110 },
                height: { xs: 92, sm: 110 },
                flexShrink: 0,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fef08a 0%, #eab308 50%, #854d0e 100%)",
                p: "5px",
                boxShadow:
                  "0 6px 20px rgba(239, 68, 68, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255,255,255,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                transform: isSpinning || spinLoading ? "scale(0.95)" : "scale(1)",
                transition: "all 0.15s ease",
                "&:hover": {
                  boxShadow:
                    "0 8px 28px rgba(239, 68, 68, 0.6), 0 2px 10px rgba(0, 0, 0, 0.2)",
                },
                "&:active": {
                  transform: "scale(0.92)",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: isSpinning
                    ? "radial-gradient(circle at 35% 30%, #f87171 0%, #dc2626 55%, #7f1d1d 100%)"
                    : "radial-gradient(circle at 35% 30%, #ef4444 0%, #b91c1c 55%, #450a0a 100%)",
                  border: "2.5px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "inset 0 4px 10px rgba(255,255,255,0.6), inset 0 -4px 10px rgba(0,0,0,0.8)",
                  animation: isSpinning ? "spinButtonPulse 0.5s infinite alternate" : "none",
                  "@keyframes spinButtonPulse": {
                    "0%": { filter: "brightness(1)" },
                    "100%": { filter: "brightness(1.3)" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    fontSize: { xs: "1.55rem", sm: "1.95rem" },
                    fontWeight: 900,
                    color: "#ffffff",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    textShadow:
                      "0 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(254, 240, 138, 0.8)",
                    transform: "translateY(-1px)",
                  }}
                >
                    {spinLoading ? "WAIT" : isSpinning ? "..." : "SPIN"}
                </Typography>
              </Box>
            </Box>

            {/* QUICK BET PLUS - Commented out since bet is fixed
            <Box
              onClick={handleIncreaseBet}
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                bgcolor: "#f8fafc",
                border: "1.5px solid #cbd5e1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
                "&:active": { transform: "scale(0.92)" },
              }}
            >
              <AddIcon sx={{ color: "#0f172a", fontSize: { xs: 26, sm: 28 } }} />
            </Box>
            */}

            {/* AUTOPLAY BUTTON */}
            <Box
              onClick={handleToggleAutoPlay}
              sx={{
                flex: 1,
                maxWidth: { xs: 140, sm: 180 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                background: isAutoSpinning
                  ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                  : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                border: isAutoSpinning ? "1.5px solid #f87171" : "1.5px solid #60a5fa",
                boxShadow: isAutoSpinning
                  ? "0 4px 12px rgba(239, 68, 68, 0.35)"
                  : "0 4px 12px rgba(59, 130, 246, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.1)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <AutorenewIcon
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, sm: 22 },
                  animation: isAutoSpinning ? "spinIcon 1s linear infinite" : "none",
                  "@keyframes spinIcon": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                {isAutoSpinning ? "STOP" : "AUTOPLAY"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 7. MODALS & POPUPS */}
      <ThreeSlotPaytableModal
        open={paytableOpen}
        onClose={() => setPaytableOpen(false)}
        activeCategory={category}
        currency={currency}
      />
      <ThreeSlotDepositModal
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        currentBalance={balance}
        onDepositSuccess={(newBal) => setBalance(newBal)}
        currency={currency}
      />
      <ThreeSlotWinModal
        open={winModalOpen}
        onClose={() => setWinModalOpen(false)}
        result={lastResult}
        currencySymbol={currency}
        betAmount={betAmount}
        onSpinAgain={() => {
          setWinModalOpen(false);
          if (!isSpinning && !spinLoading) {
            handleSpin();
          }
        }}
      />
      <FailedPopup
        open={failedModal.open}
        show={failedModal.open}
        setShowModal={(open) => setFailedModal((prev) => ({ ...prev, open }))}
        onClose={() => setFailedModal((prev) => ({ ...prev, open: false }))}
        title={failedModal.title}
        message={failedModal.message}
        description={failedModal.description}
        btnName="Close"
      />
    </Box>
  );
};

export default ThreeSlotMachineDetailCard;